import { useCallback, useContext } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    clickBannerCarouselEvent,
    clickCloseDineoutPaymentSuccess,
    clickViewHelpDineoutPaymentSuccess,
    impressionBannerCarouselEvent,
    impressionDineoutPaymentSuccess,
    paymentCartClickEvent,
    savingsTextImpressionEvent,
    impressionSplitBillWidget,
    clickSplitBillWidget,
} from "@dineout-payment-success-ui/analytics/events";
import { ANALYTICS_CONSTANTS } from "@dineout-payment-success-ui/constants";
import { DO_CLICK_ACTIONS, SCREEN_NAME } from "@dineout-payment-success-ui/analytics/constants";

export const useDineoutPaymentSuccess = () => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const onClickCloseDineoutPaymentSuccess = useCallback(
        (label, orderId) => {
            const isClose = label === ANALYTICS_CONSTANTS.CLOSE;
            clickCloseDineoutPaymentSuccess(clickEvent, isClose, orderId);
        },
        [clickEvent],
    );

    const onClickViewHelpDineoutPaymentSuccess = useCallback(
        (orderId) => {
            clickViewHelpDineoutPaymentSuccess(clickEvent, orderId);
        },
        [clickEvent],
    );

    const onImpressionDineoutPaymentSuccess = useCallback(
        (data) => {
            impressionDineoutPaymentSuccess(impressionEvent, data);
        },
        [impressionEvent],
    );

    const onCarouselItemClick = useCallback(
        (bannerId) => {
            clickBannerCarouselEvent(clickEvent, bannerId);
        },
        [impressionEvent],
    );

    const onCarouselItemView = useCallback(
        (bannerId) => {
            impressionBannerCarouselEvent(impressionEvent, bannerId);
        },
        [impressionEvent],
    );

    const onSavingsTextImpression = useCallback(
        (data) => {
            savingsTextImpressionEvent(impressionEvent, data);
        },
        [impressionEvent],
    );

    const onImpressionSplitBillWidget = useCallback(
        (data) => {
            impressionSplitBillWidget(impressionEvent, data);
        },
        [impressionEvent],
    );

    const onClickSplitBillWidget = useCallback(
        (data) => {
            clickSplitBillWidget(clickEvent, data);
        },
        [clickEvent],
    );

    return {
        onClickCloseDineoutPaymentSuccess,
        onClickViewHelpDineoutPaymentSuccess,
        onImpressionDineoutPaymentSuccess,
        onCarouselItemClick,
        onCarouselItemView,
        onSavingsTextImpression,
        onImpressionSplitBillWidget,
        onClickSplitBillWidget,
    };
};

export const useTipAnalytics = () => {
    const { clickEvent } = useContext(AnalyticsContext);

    const onTipClicked = useCallback(() => {
        paymentCartClickEvent({
            clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: DO_CLICK_ACTIONS.ADD_TIP,
            label: DO_CLICK_ACTIONS.ADD_TIP_CLICK,
            context: "",
        });
    }, [clickEvent]);

    const onTipClosed = useCallback(() => {
        paymentCartClickEvent({
            clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: DO_CLICK_ACTIONS.CUSTOM_TIP,
            label: DO_CLICK_ACTIONS.CLOSE,
            context: "",
        });
    }, [clickEvent]);

    const onCustomTipNextClick = useCallback(
        (tipVal: number) => {
            paymentCartClickEvent({
                clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: DO_CLICK_ACTIONS.CUSTOM_TIP,
                label: DO_CLICK_ACTIONS.NEXT,
                context: `${tipVal}`,
            });
        },
        [clickEvent],
    );

    const onClearTipClick = useCallback(
        (tipVal: number) => {
            paymentCartClickEvent({
                clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: DO_CLICK_ACTIONS.CLEAR_TIP,
                label: DO_CLICK_ACTIONS.CLEAR_TIP,
                context: `${tipVal}`,
            });
        },
        [clickEvent],
    );

    const onClickTipPredefined = useCallback(
        (tipVal: number) => {
            paymentCartClickEvent({
                clickEvent,
                category: SCREEN_NAME.PAYMENT_CART,
                action: DO_CLICK_ACTIONS.TIP_PREDEFINED,
                label: "",
                context: `${tipVal}`,
            });
        },
        [clickEvent],
    );

    const onClickCustomTip = useCallback(() => {
        paymentCartClickEvent({
            clickEvent,
            category: SCREEN_NAME.PAYMENT_CART,
            action: DO_CLICK_ACTIONS.CUSTOM_TIP_CLICK,
            label: "",
            context: "",
        });
    }, [clickEvent]);

    return {
        onCustomTipNextClick,
        onClearTipClick,
        onClickTipPredefined,
        onClickCustomTip,
        onTipClicked,
        onTipClosed,
    };
};
